import { useEvent } from '@shared-core/hooks/use-event'
import {
  EXIT_FLOW_PARAM_KEY,
  isSheet,
  type NavigationAdapter,
  type NavKey,
  navigation,
  readExitFlowMeta,
  ScreensList,
  StackMode,
  setScrollHistoryKey,
  withExitFlowParams,
} from '@shared-core/navigation'
import { SCREENS_PATHS, type ScreenRouteKey } from '@shared-core/navigation/screens-paths'
import { sheetNavGuard, sheetStore } from '@shared-core/navigation/sheet-store'
import { preloadScreen } from '@web/libs/navigation/screen-preload'
import { WebRouteContext } from '@web/libs/navigation/web-route-context'
import { router } from '@web/router'
import { homePath } from '@web/router/routes-screens'
import {
  isSheetScreenKey,
  SHEET_SCREEN_PATHS,
  type SheetScreenKey,
  sheetScreenPathParamNames,
} from '@web/router/routes-sheets'
import { SheetContext } from '@web/sheets/context'
import { isRegisteredSheetKey, SHEET_REGISTRY } from '@web/sheets/registry'
import { useContext, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { generatePath, matchPath } from 'react-router-dom'

function useRouterLocation() {
  return useSyncExternalStore(
    (onStoreChange) => router.subscribe(onStoreChange),
    () => router.state.location,
    () => router.state.location,
  )
}

type NestedNavParams = { screen?: ScreenRouteKey; params?: unknown }
type NestedSheetParams = { screen?: NavKey; params?: unknown }
// key: NavKey — кроме FORM_SHEET_* сюда попадают popup-экраны (options.popup на mobile,
// например phones info/delete), которые web показывает шторкой через sheetStore.
type SheetTarget = { key: NavKey; params?: unknown }

// Ключ презентуется шторкой: form sheet ЛИБО экран, зарегистрированный в web sheet-реестре
// (мобильный options.popup для экранов — phones info/delete).
function isSheetLikeKey(key: NavKey): boolean {
  return isSheet(key) || isRegisteredSheetKey(key)
}

// Полноэкранный sheet-screen или компактный оверлей — решает реестр (routes-sheets).
function isFullscreenSheetScreen(key: NavKey): boolean {
  return isSheetScreenKey(key)
}

// Роут-ключ web: обычные экраны + «шиты-экраны» (полноэкранные form sheets мобилы).
type WebRouteKey = ScreenRouteKey | SheetScreenKey
const WEB_SCREEN_PATHS: Record<string, string> = { ...SCREENS_PATHS, ...SHEET_SCREEN_PATHS }

// На mobile вложенные стеки (exchange, tabs). На web — плоские path: разворачиваем до leaf-экрана.
function resolveScreenTarget(key: NavKey, params?: unknown): { key: WebRouteKey; state?: unknown } {
  if (key === StackMode.AUTHORIZED || key === StackMode.LOGOUT) {
    const nested = params as NestedNavParams | undefined

    if (nested?.screen) {
      return resolveScreenTarget(nested.screen, nested.params)
    }

    return {
      key: ScreensList.HOME_START as ScreenRouteKey,
      state: nested?.params,
    }
  }

  const nested = params as NestedNavParams | undefined

  if (nested?.screen && SCREENS_PATHS[nested.screen]) {
    const childKey = nested.screen
    const childPath = SCREENS_PATHS[childKey]
    const parentPath = SCREENS_PATHS[key as ScreenRouteKey]

    if (childPath !== parentPath) {
      return resolveScreenTarget(childKey, nested.params)
    }
  }

  return { key: key as ScreenRouteKey, state: params }
}

// На mobile часть форм-шитов — вложенные stack-навигаторы (dispute, swap, topup):
// вызов там navigate(PARENT_SHEET, { screen: CHILD_SHEET, params }). На web вложенных
// стеков нет — разворачиваем до конечного (leaf) шита, иначе откроется дефолтный
// экран родителя, а params со `screen` уйдут не туда.
function resolveSheetTarget(key: NavKey, params?: unknown): SheetTarget {
  const nested = params as NestedSheetParams | undefined

  if (nested?.screen && isSheet(nested.screen)) {
    return resolveSheetTarget(nested.screen, nested.params)
  }

  return { key, params }
}

let sheetOpenRequestId = 0

function cancelPendingSheetOpen() {
  sheetOpenRequestId += 1
}

function preloadSheet(key: NavKey): Promise<void> {
  if (!isRegisteredSheetKey(key)) return Promise.resolve()

  return SHEET_REGISTRY[key].preload()
}

function openSheetAfterPreload(target: SheetTarget, open: () => void) {
  const requestId = ++sheetOpenRequestId

  void preloadSheet(target.key).then(
    () => {
      if (requestId === sheetOpenRequestId) open()
    },
    () => undefined,
  )
}

// Паттерн pop()+navigate (например select-topup → external wallet): goBack уже поставил
// history.back() в очередь, шит доигрывает exit-анимацию (isAnimating). Пушить роут сразу
// нельзя — отложенный back откатит его. Ждём popstate от этого back (или конца анимации,
// если back уже обработан) и только потом навигируем.
function runAfterPendingSheetClose(run: () => void) {
  let done = false

  const finish = () => {
    if (done) return
    done = true
    window.removeEventListener('popstate', onPopstate)
    unsubscribe()
    clearTimeout(fallbackTimer)
    run()
  }

  const onPopstate = () => finish()
  const unsubscribe = sheetStore.subscribe(() => {
    if (!sheetStore.isAnimating()) finish()
  })
  // Предохранитель: если back уже был обработан (закрытие пришло из popstate) —
  // ни popstate, ни emit может не случиться; навигация не должна потеряться.
  const fallbackTimer = setTimeout(finish, 250)

  window.addEventListener('popstate', onPopstate)
}

// Паттерн роута react-router для sheet-screen с path-параметрами: /transaction/:txHash,
// /topup/wallet/:coin/:network.
function sheetScreenPattern(base: string, names: string[]): string {
  return `/${base}/${names.map((name) => `:${name}`).join('/')}`
}

// Путь экрана. Для sheet-screen с path-параметром собираем сегменты из params
// через generatePath (нативная сборка + энкодинг): /transaction/{txHash}. Так URL указывает
// на сущность, шарится и открывается напрямую.
function screenPath(routeKey: WebRouteKey, params?: unknown): string {
  const path = WEB_SCREEN_PATHS[routeKey]

  if (!path) {
    return homePath
  }

  const names = sheetScreenPathParamNames(routeKey)
  const record = params && typeof params === 'object' ? (params as Record<string, unknown>) : null

  if (!names.length || !record) {
    return `/${path}`
  }

  const values: Record<string, string> = {}

  for (const name of names) {
    const value = record[name]

    if (typeof value !== 'string' || !value) {
      return `/${path}`
    }

    values[name] = value
  }

  return generatePath(sheetScreenPattern(path, names), values)
}

// Матч pathname против sheet-screens с path-параметрами через нативный matchPath (точный матч
// + декодинг): /transaction/{txHash} → ключ + значения.
function matchSheetScreenPathParam(pathname: string): { key: string; params: Record<string, string> } | null {
  for (const key of Object.keys(SHEET_SCREEN_PATHS) as SheetScreenKey[]) {
    const names = sheetScreenPathParamNames(key)

    if (!names.length) continue

    const matched = matchPath(sheetScreenPattern(SHEET_SCREEN_PATHS[key], names), pathname)?.params

    if (!matched) continue

    const params: Record<string, string> = {}
    const isComplete = names.every((name) => {
      const value = matched[name]

      if (!value) return false

      params[name] = value
      return true
    })

    if (!isComplete) continue

    return { key, params }
  }

  return null
}

export function pathnameToScreenKey(pathname: string): string {
  const normalized = pathname.replace(/^\/+|\/+$/g, '')

  for (const [key, path] of Object.entries(WEB_SCREEN_PATHS)) {
    if (path === normalized) return key
  }

  return matchSheetScreenPathParam(pathname)?.key ?? normalized
}

// Восстанавливаем вынесенный в путь param (txHash) из pathname. Нужно и при обычной навигации,
// и при холодной загрузке прямой ссылки (history.state пуст → param только из URL).
function sheetScreenPathParams(pathname: string): Record<string, string> {
  return matchSheetScreenPathParam(pathname)?.params ?? {}
}

// На mobile (react-navigation) params живут в памяти, поэтому экраны принимают callback'и
// (onSelect/onContinue/setMethod). На web навигация экрана идёт через history.state, который
// структурно клонируется и НЕ переживает функции (DataCloneError). Поэтому params с функциями
// держим в in-memory map, а в history кладём только сериализуемый id (как делает sheetStore для шитов).
const SCREEN_PARAMS_ID_KEY = '__screenParamsId'
let screenParamsSeq = 0
const screenParamsById = new Map<string, unknown>()
let cancelPendingHistoryPop: () => void = () => undefined

function paramsContainFunction(params: unknown): boolean {
  if (!params || typeof params !== 'object') return false

  return Object.values(params as Record<string, unknown>).some((value) => typeof value === 'function')
}

function stashScreenParams(params: unknown): Record<string, string> {
  screenParamsSeq += 1
  const id = `screen-params-${screenParamsSeq}`
  screenParamsById.set(id, params)

  return { [SCREEN_PARAMS_ID_KEY]: id }
}

// Восстанавливаем params для useRoute: по id из in-memory map, иначе — сам state (сериализуемый случай).
function resolveScreenParams(state: unknown): Record<string, unknown> {
  if (state && typeof state === 'object' && SCREEN_PARAMS_ID_KEY in state) {
    const id = (state as Record<string, string>)[SCREEN_PARAMS_ID_KEY]

    return (screenParamsById.get(id) as Record<string, unknown>) ?? {}
  }

  return (state as Record<string, unknown> | null) ?? {}
}

function navigateToScreen(key: NavKey, params?: unknown, options?: { replace?: boolean }) {
  cancelPendingHistoryPop()
  cancelPendingSheetOpen()
  const target = resolveScreenTarget(key, params)
  // Функции в state роняют history.pushState — подменяем на id, иначе кладём params как есть.
  const state = paramsContainFunction(target.state) ? stashScreenParams(target.state) : (target.state ?? undefined)

  // Путь строим из сырых params (target.state), а не из stash-id: значение (txHash) обязано
  // попасть в URL сегментом, чтобы ссылку можно было скопировать/шарить и открыть напрямую.
  router.navigate(screenPath(target.key, target.state), {
    state,
    replace: options?.replace,
  })
}

// Доступ к фасаду без возни с generic-кортежами аргументов.
const nav = navigation as {
  navigate: (key: NavKey, params?: unknown) => void
  popTo: (key: NavKey, params?: unknown) => void
  reset: (key: NavKey, params?: unknown) => void
  goBack: () => void
  popToTop: () => void
}

function currentHistoryIdx(): number | null {
  const idx = (window.history.state as { idx?: number } | null)?.idx

  return typeof idx === 'number' ? idx : null
}

function routerCanGoBack(): boolean {
  const idx = currentHistoryIdx()

  return idx !== null ? idx > 0 : window.history.length > 1
}

// Трекинг стека истории (idx → pathname). Браузерный history API не даёт прочитать прошлые
// записи, а popTo должен ВЕРНУТЬСЯ к экрану (history.go(-N)), а не запушить его дубликат.
// Ведём карту, слушая router; при PUSH затираем forward-ветку (браузер её инвалидирует).
const historyPathByIdx = new Map<number, string>()

function trackHistoryLocation() {
  // Синхронно до ре-рендера RouterProvider — ключ актуален к монтированию экрана (см. useScrollRestoration).
  setScrollHistoryKey(router.state.location.key)

  const idx = currentHistoryIdx()

  if (idx === null) return

  historyPathByIdx.set(idx, router.state.location.pathname)

  if (router.state.historyAction === 'PUSH') {
    for (const existingIdx of [...historyPathByIdx.keys()]) {
      if (existingIdx > idx) historyPathByIdx.delete(existingIdx)
    }
  }
}

trackHistoryLocation()
router.subscribe(trackHistoryLocation)

// Сколько шагов назад до ближайшего вхождения экрана в истории (отрицательное число для
// history.go), либо null — если экрана позади нет (тогда popTo падает в обычный push).
function historyStepsBackTo(key: NavKey, params?: unknown): number | null {
  const currentIdx = currentHistoryIdx()

  if (currentIdx === null) return null

  const resolvedTarget = resolveScreenTarget(key, params)
  const targetPath = screenPath(resolvedTarget.key, resolvedTarget.state)

  for (let idx = currentIdx - 1; idx >= 0; idx -= 1) {
    if (historyPathByIdx.get(idx) === targetPath) {
      return idx - currentIdx
    }
  }

  return null
}

// history.go не умеет передавать новый state. Если передали params — после POP заменяем
// state целевой записи (как StackActions.popTo(name, params) на mobile). Без params
// запись не трогаем: иначе exit-meta/колбэки цели сотрутся.
function popToHistoryEntry(key: NavKey, params: unknown, stepsBack: number) {
  const currentIdx = currentHistoryIdx()

  if (currentIdx === null) {
    navigateToScreen(key, params)
    return
  }

  cancelPendingHistoryPop()

  const startIdx = currentIdx
  const targetIdx = currentIdx + stepsBack
  const resolvedTarget = resolveScreenTarget(key, params)
  const targetPath = screenPath(resolvedTarget.key, resolvedTarget.state)
  let isCancelled = false
  let unsubscribe: () => void = () => undefined

  const cancel = () => {
    if (isCancelled) return

    isCancelled = true
    unsubscribe()

    if (cancelPendingHistoryPop === cancel) cancelPendingHistoryPop = () => undefined
  }

  cancelPendingHistoryPop = cancel
  unsubscribe = router.subscribe(() => {
    if (router.state.historyAction !== 'POP') return

    // Откат шитов (clearOpenSheetsRewindingHistory) даёт свой POP уже после того, как мы
    // подписались: router обрабатывает popstate асинхронно. Он остаётся на startIdx —
    // это не наш go(), иначе подписка снялась бы до реального попадания в цель.
    if (currentHistoryIdx() === startIdx && startIdx !== targetIdx) return

    const didReachTarget = currentHistoryIdx() === targetIdx && router.state.location.pathname === targetPath

    cancel()

    if (!didReachTarget) return

    // Params не передали — оставляем state цели как есть (meta сохранится сама).
    if (params === undefined) return

    // POP_TO перезаписывает params цели, но не её exit-meta: возврат назад не должен
    // «углублять» hop (как withPopToParams на mobile).
    const existingMeta = readExitFlowMeta(resolveScreenParams(router.state.location.state))
    const nextParams = existingMeta ? { ...(params as object), [EXIT_FLOW_PARAM_KEY]: existingMeta } : params

    navigateToScreen(key, nextParams, { replace: true })
  })

  router.navigate(stepsBack)
}

type ResetRoute = {
  name?: string
  params?: unknown
  state?: {
    index?: number
    routes?: ResetRoute[]
  }
}

type ResetAction = {
  type: string
  payload?: {
    index?: number
    routes?: ResetRoute[]
  }
}

function resolveResetRoute(route: ResetRoute | undefined): { key: NavKey; params?: unknown } | null {
  if (!route?.name) return null

  if (route.name === StackMode.AUTHORIZED || route.name === StackMode.LOGOUT) {
    const nestedRoutes = route.state?.routes
    const nestedIndex = route.state?.index ?? (nestedRoutes ? nestedRoutes.length - 1 : 0)
    const nested = nestedRoutes?.[nestedIndex]

    return resolveResetRoute(nested)
  }

  return { key: route.name as NavKey, params: route.params }
}

function handleNavigationDispatch(action: unknown) {
  if (!action || typeof action !== 'object' || !('type' in action)) return

  const typed = action as ResetAction

  if (typed.type !== 'RESET' || !typed.payload?.routes?.length) return

  const routes = typed.payload.routes
  const index = Math.min(Math.max(typed.payload.index ?? routes.length - 1, 0), routes.length - 1)
  const target = resolveResetRoute(routes[index])

  if (!target) return

  // Exit-cross шлёт CommonActions.reset(origin). На web обычный reset = replace текущего
  // URL → промежуточные шаги флоу остаются в history, и «назад» с origin уходит обратно
  // в флоу (Security ← 2FA). Если origin уже позади — popTo (history.go).
  if (historyStepsBackTo(target.key, target.params) !== null) {
    nav.popTo(target.key, target.params)
    return
  }

  nav.reset(target.key, target.params)
}

// react-navigation-совместимый объект навигации поверх фасада.
function useWebNavigation() {
  return useMemo(
    () => ({
      navigate: (key: NavKey, params?: unknown) => nav.navigate(key, params),
      goBack: () => nav.goBack(),
      pop: () => nav.goBack(),
      popTo: (key: NavKey, params?: unknown) => nav.popTo(key, params),
      popToTop: () => nav.popToTop(),
      push: (key: NavKey, params?: unknown) => nav.navigate(key, params),
      replace: (key: NavKey, params?: unknown) => nav.reset(key, params),
      reset: (key: NavKey, params?: unknown) => nav.reset(key, params),
      dispatch: (action: unknown) => handleNavigationDispatch(action),
      canGoBack: () => sheetStore.hasOpen() || routerCanGoBack(),
      preload(screen: NavKey, params?: unknown) {
        // Прогреваем lazy-чанк экрана, чтобы переход не ждал загрузку (например разворот
        // блока Сервисов с Главной). Вложенный вызов navigate-формата разворачиваем.
        const nested = (params as NestedNavParams | undefined)?.screen
        preloadScreen(nested ?? screen)
      },
      setParams: (params: Record<string, unknown>) => {
        // Параметры шита живут в памяти (sheetStore) и иммутабельны — для них no-op.
        if (sheetStore.hasOpen()) return

        const { location } = router.state
        const locationState = location.state

        // Params уже в стэше (были функции) — обновляем запись по id на месте:
        // колбэки сохраняются, history.state не меняется.
        if (locationState && typeof locationState === 'object' && SCREEN_PARAMS_ID_KEY in locationState) {
          const id = (locationState as Record<string, string>)[SCREEN_PARAMS_ID_KEY]
          const currentParams = (screenParamsById.get(id) as Record<string, unknown> | undefined) ?? {}

          screenParamsById.set(id, { ...currentParams, ...params })
          void router.navigate(location.pathname + location.search, { replace: true, state: locationState })
          return
        }

        const merged = { ...resolveScreenParams(locationState), ...params }
        const state = paramsContainFunction(merged) ? stashScreenParams(merged) : merged

        void router.navigate(location.pathname + location.search, { replace: true, state })
      },
      setOptions: (_options: object) => {
        // Фон шита задаёт vaul в SheetContainer; setOptions не нужен на web.
      },
      // Событий навигации на web нет: экраны живут в react-router, шиты — в sheetStore,
      // общей шины beforeRemove/focus у них нет. Возвращаем no-op отписку, иначе общий код,
      // который по типу NavigationLike вправе подписаться, падает с TypeError.
      addListener: (_type: string, _callback: (...args: unknown[]) => void) => () => {},
      removeListener: (_type: string, _callback: (...args: unknown[]) => void) => {},
    }),
    [],
  )
}

/**
 * Route экрана, на котором компонент смонтирован (а не тот, что сейчас в location).
 *
 * react-router v7 гоняет переходы через `startTransition`, поэтому уходящий экран живёт,
 * пока грузится lazy-чанк следующего (см. RouteTransitionOutlet). Location при этом читается
 * через useSyncExternalStore — он обновляется СИНХРОННО, вне транзишена, и уходящий экран
 * успевает ре-рендериться с params чужого маршрута. На mobile такого не бывает: params
 * экрана в стеке не меняются от того, что сверху запушили другой.
 *
 * Поэтому params фиксируем по экрану монтирования и обновляем, только пока location всё ещё
 * указывает на него (replace / setParams / смена сегмента пути).
 */
function useOwnRoute(location: ReturnType<typeof useRouterLocation>) {
  const name = pathnameToScreenKey(location.pathname)
  const ownRef = useRef<{ key: string; name: string; params: Record<string, unknown> } | null>(null)

  if (!ownRef.current || ownRef.current.name === name) {
    ownRef.current = {
      key: location.key,
      name,
      // URL-сегмент (txHash) мержим под state: при холодной загрузке прямой ссылки state пуст,
      // а при обычной навигации state с полным набором params перекрывает те же ключи.
      params: {
        ...sheetScreenPathParams(location.pathname),
        ...resolveScreenParams(location.state),
      },
    }
  }

  return ownRef.current
}

// route с полем params (текущий экран или шит). Совместимо с useRoute().params.
function useWebRoute() {
  const ctx = useContext(SheetContext)
  const webRouteCtx = useContext(WebRouteContext)
  const location = useRouterLocation()
  const ownRoute = useOwnRoute(location)

  // Шит поверх экрана — свои params из SheetContext.
  if (ctx) {
    return { key: ctx.id, name: ctx.id, params: ctx.params }
  }

  if (webRouteCtx) {
    return webRouteCtx.route
  }

  return ownRoute
}

function useWebIsFocused(): boolean {
  return useContext(WebRouteContext)?.isFocused ?? true
}

function buildWebNavigationState(location: ReturnType<typeof useRouterLocation>) {
  const screenRoute = {
    key: location.key,
    name: pathnameToScreenKey(location.pathname),
    params: {
      ...sheetScreenPathParams(location.pathname),
      ...((location.state as Record<string, unknown> | null) ?? {}),
    },
  }

  const sheetRoutes = sheetStore
    .getSnapshot()
    .filter((sheet) => !sheet.closing)
    .map((sheet) => ({
      key: sheet.id,
      name: sheet.key,
      params: sheet.params,
    }))

  const routes = [screenRoute, ...sheetRoutes]

  return {
    key: 'web-root',
    index: routes.length - 1,
    routeNames: routes.map((route) => route.name),
    routes,
    type: 'stack',
    stale: false as const,
  }
}

function useWebNavigationState<T>(selector: (state: unknown) => T): T {
  const location = useRouterLocation()
  const sheets = useSyncExternalStore(sheetStore.subscribe, sheetStore.getSnapshot, sheetStore.getSnapshot)
  const state = useMemo(() => buildWebNavigationState(location), [location, sheets])

  return selector(state)
}

function useWebFocusEffect(effect: () => undefined | (() => void)): void {
  const isFocused = useWebIsFocused()

  useEffect(() => {
    if (!isFocused) return undefined

    return effect()
  }, [effect, isFocused])
}

// Шит пишет пустой pushState (тот же URL). Сброс только sheetStore оставляет orphan-записи
// в history → следующий back/exit-cross промахивается мимо origin. Откатываем их вместе со стеком.
function clearOpenSheetsRewindingHistory(): boolean {
  const count = sheetStore.getSnapshot().reduce((c, item) => c + (item.closing ? 0 : 1), 0)
  sheetStore.reset()

  if (count <= 0) return false

  // Стек уже пуст — SheetHost на popstate no-op (hasOpen=false).
  window.history.go(-count)
  return true
}

function useWebCloseFormSheets() {
  return useEvent(() => {
    cancelPendingSheetOpen()
    clearOpenSheetsRewindingHistory()
  })
}

function blurFocusedElementBeforeSheetOpen(): void {
  const activeElement = document.activeElement

  if (
    activeElement instanceof HTMLElement &&
    activeElement !== document.body &&
    activeElement !== document.documentElement
  ) {
    activeElement.blur()
  }
}

function getCurrentRouteSnapshot() {
  const sheets = sheetStore.getSnapshot().filter((sheet) => !sheet.closing)

  if (sheets.length > 0) {
    const top = sheets[sheets.length - 1]
    return { name: top.key, params: top.params }
  }

  const location = router.state.location

  return {
    name: pathnameToScreenKey(location.pathname),
    params: {
      ...sheetScreenPathParams(location.pathname),
      ...resolveScreenParams(location.state),
    },
  }
}

function withMeta(key: NavKey, params?: unknown, isReplace = false) {
  return withExitFlowParams({
    targetKey: key,
    params,
    current: getCurrentRouteSnapshot(),
    options: isReplace ? { isReplace: true } : undefined,
  })
}

// Web-реализация порта навигации: экран → react-router (path), формшит → in-memory стек.
// Устанавливается в entry: installNavigation(WebNavigationAdapter).
export const WebNavigationAdapter: NavigationAdapter = {
  navigate(key, params) {
    cancelPendingHistoryPop()
    const nextParams = withMeta(key, params)

    if (isSheetLikeKey(key)) {
      const target = resolveSheetTarget(key, nextParams)

      // «Шит-экран» (полноэкранный form sheet мобилы) — это обычный роут: идёт в history,
      // остаётся под следующими экранами и виден при возврате назад.
      if (isFullscreenSheetScreen(target.key)) {
        // pop()+navigate: пуш до обработки отложенного history.back() был бы им откачен.
        if (sheetStore.isAnimating()) {
          runAfterPendingSheetClose(() => navigateToScreen(target.key, target.params))
          return
        }

        // Шиты снимаем вместе с их pushState: иначе orphan-запись остаётся между текущим
        // экраном и новым, и первый «назад» с нового экрана попадает в неё (визуально no-op).
        // cancel до откладывания: иначе догружающийся шит успеет открыться в окне ожидания.
        cancelPendingSheetOpen()

        if (clearOpenSheetsRewindingHistory()) {
          runAfterPendingSheetClose(() => navigateToScreen(target.key, target.params))
          return
        }

        navigateToScreen(target.key, target.params)
        return
      }

      openSheetAfterPreload(target, () => {
        if (sheetStore.isAnimating()) {
          sheetNavGuard.suppressNextPopstateClose()
          blurFocusedElementBeforeSheetOpen()
          sheetStore.replaceAfterClose(target.key, target.params)
          window.history.pushState(window.history.state, '')
          return
        }

        if (sheetStore.hasOpen()) {
          blurFocusedElementBeforeSheetOpen()
          sheetStore.open(target.key, target.params)
          return
        }

        blurFocusedElementBeforeSheetOpen()
        sheetStore.open(target.key, target.params)
      })
      return
    }

    // pop()+navigate(SCREEN) (например phones info → replace number): отложенный
    // history.back откатил бы свежий push — ждём его обработки.
    if (sheetStore.isAnimating()) {
      runAfterPendingSheetClose(() => navigateToScreen(key, nextParams))
      return
    }

    cancelPendingSheetOpen()

    if (clearOpenSheetsRewindingHistory()) {
      runAfterPendingSheetClose(() => navigateToScreen(key, nextParams))
      return
    }

    navigateToScreen(key, nextParams)
  },

  // Переход с шита-источника: popup-шиты гасим через navigate; «шит-экраны» (transaction,
  // verify/sms) — replace текущего URL, иначе goBack()+push гоняются с отложенным history.back.
  navigateFromSheet(key, params) {
    cancelPendingHistoryPop()
    // Шит заменяется целевым экраном (тот же слот глубины) → isReplace, hop не +1.
    const nextParams = withMeta(key, params, true)
    const currentKey = pathnameToScreenKey(window.location.pathname)

    if (isSheetScreenKey(currentKey as NavKey)) {
      cancelPendingSheetOpen()

      if (sheetStore.isAnimating()) {
        runAfterPendingSheetClose(() => navigateToScreen(key, nextParams, { replace: true }))
        return
      }

      if (clearOpenSheetsRewindingHistory()) {
        runAfterPendingSheetClose(() => navigateToScreen(key, nextParams, { replace: true }))
        return
      }

      navigateToScreen(key, nextParams, { replace: true })
      return
    }

    this.navigate(key, params)
  },

  // Web: шит-экраны — обычные роуты, прятать нечего; history разрулят popTo / navigateFromSheet.
  dismissSheetsBelowCurrent() {
    return
  },

  // Web: шиты уже гасятся в navigate/navigateFromSheet — тот же путь, что с шита-источника.
  navigateDismissingSheets(key, params) {
    this.navigateFromSheet(key, params)
  },

  goBack() {
    cancelPendingHistoryPop()
    cancelPendingSheetOpen()

    // Открыт шит — закрываем сразу и синхронизируем history (popstate может не сработать повторно).
    if (sheetStore.hasOpen()) {
      sheetStore.closeTop()
      sheetNavGuard.suppressNextPopstateClose()
      window.history.back()
      return
    }

    // Зависший closing-шит не должен блокировать возврат на экран.
    sheetStore.clearClosing()

    router.navigate(-1)
  },

  popTo(key, params) {
    cancelPendingHistoryPop()
    const hadPendingClose = sheetStore.isAnimating()

    // Цель — шит (например replace одного формшита другим): открываем как оверлей, не как роут.
    // History шитов не откатываем здесь — новый open сам пишет pushState.
    if (isSheetLikeKey(key)) {
      sheetStore.reset()
      const target = resolveSheetTarget(key, params)

      if (isSheet(target.key) && isFullscreenSheetScreen(target.key)) {
        cancelPendingSheetOpen()

        if (hadPendingClose) {
          runAfterPendingSheetClose(() => navigateToScreen(target.key, target.params))
          return
        }

        navigateToScreen(target.key, target.params)
        return
      }

      openSheetAfterPreload(target, () => {
        blurFocusedElementBeforeSheetOpen()
        sheetStore.open(target.key, target.params)
      })
      return
    }

    cancelPendingSheetOpen()

    const runScreenPopTo = () => {
      if (key === StackMode.AUTHORIZED || key === StackMode.LOGOUT) {
        navigateToScreen(key, params)
        return
      }

      const resolvedTarget = resolveScreenTarget(key, params)
      const targetPath = screenPath(resolvedTarget.key, resolvedTarget.state)

      // Как и mobile popTo, обновляем params без создания дубликата, если цель уже открыта.
      if (router.state.location.pathname === targetPath) {
        navigateToScreen(key, params, { replace: true })
        return
      }

      // Настоящий «pop to»: если экран уже есть в истории позади — возвращаемся к нему
      // (history.go(-N), затем replace params; exit-meta цели не пересчитываем через withMeta).
      // Иначе (экрана позади нет) — обычный push с meta вперёд.
      const stepsBack = historyStepsBackTo(key, params)

      if (stepsBack !== null) {
        popToHistoryEntry(key, params, stepsBack)
        return
      }

      navigateToScreen(key, withMeta(key, params))
    }

    // Сначала снимаем шиты + их history, иначе stepsBack считается «сквозь» orphan pushState.
    const didRewindSheets = clearOpenSheetsRewindingHistory()

    if (didRewindSheets || hadPendingClose) {
      runAfterPendingSheetClose(runScreenPopTo)
      return
    }

    runScreenPopTo()
  },

  popToTop() {
    cancelPendingHistoryPop()
    cancelPendingSheetOpen()

    const goHome = () => {
      router.navigate('/')
    }

    if (clearOpenSheetsRewindingHistory()) {
      runAfterPendingSheetClose(goHome)
      return
    }

    goHome()
  },

  reset(key, params) {
    cancelPendingHistoryPop()
    // Snapshot до очистки шитов — иначе потеряем origin/hop текущего контекста.
    // Web replace тоже идёт через reset → hop не инкрементим (replace: true).
    const nextParams = withMeta(key, params, true)
    const hadPendingClose = sheetStore.isAnimating()

    // Цель — шит (например replace одного формшита другим): открываем как оверлей, не как роут.
    if (isSheetLikeKey(key)) {
      sheetStore.reset()
      const target = resolveSheetTarget(key, nextParams)

      if (isSheet(target.key) && isFullscreenSheetScreen(target.key)) {
        cancelPendingSheetOpen()

        if (hadPendingClose) {
          runAfterPendingSheetClose(() => navigateToScreen(target.key, target.params, { replace: true }))
          return
        }

        navigateToScreen(target.key, target.params, { replace: true })
        return
      }

      openSheetAfterPreload(target, () => {
        blurFocusedElementBeforeSheetOpen()
        sheetStore.open(target.key, target.params)
      })
      return
    }

    cancelPendingSheetOpen()

    const replaceScreen = () => {
      navigateToScreen(key, nextParams, { replace: true })
    }

    const didRewindSheets = clearOpenSheetsRewindingHistory()

    if (didRewindSheets || hadPendingClose) {
      runAfterPendingSheetClose(replaceScreen)
      return
    }

    replaceScreen()
  },

  resetToHomeWithSheet(key, params) {
    cancelPendingHistoryPop()
    cancelPendingSheetOpen()
    const target = resolveSheetTarget(key, withMeta(key, params, true))
    const hadPendingClose = sheetStore.isAnimating()
    const didRewindSheets = clearOpenSheetsRewindingHistory()
    const showResult = () => {
      cancelPendingSheetOpen()
      const requestId = sheetOpenRequestId

      void router.navigate(homePath, { replace: true }).then(() => {
        if (requestId !== sheetOpenRequestId) return

        openSheetAfterPreload(target, () => {
          blurFocusedElementBeforeSheetOpen()
          sheetStore.open(target.key, target.params)
        })
      })
    }

    if (didRewindSheets || hadPendingClose) {
      runAfterPendingSheetClose(showResult)
      return
    }

    showResult()
  },

  getCurrentRouteName() {
    return sheetStore.topKey() ?? pathnameToScreenKey(window.location.pathname)
  },

  preload(_key: ScreensList, _params?: unknown) {
    // не делаем
  },

  useNavigation: useWebNavigation,
  useRoute: useWebRoute,
  useIsFocused: useWebIsFocused,
  useFocusEffect: useWebFocusEffect,
  useNavigationState: useWebNavigationState,
  useCloseFormSheets: useWebCloseFormSheets,
}
